<template>
  <public-layout
    title="Browse Listings"
    subtitle="Discover construction projects, real estate properties, and architectural concepts"
  >
    <!-- Search bar injected into the hero gradient -->
    <template #hero-extra>
      <div class="sc-page-search">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
        <input
          type="text"
          v-model="search"
          placeholder="Search listings by name or location..."
          @input="onSearchInput"
        />
        <button v-if="search" class="sc-page-search__clear" @click="clearSearch">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>
    </template>

    <!-- ── Body ── -->
    <div class="sc-container sc-listings-body">

      <!-- Filter tabs -->
      <div class="sc-listings-toolbar">
        <div class="filter-tabs">
          <button v-for="tab in tabs" :key="tab.value"
            :class="{ active: activeType === tab.value }"
            @click="setType(tab.value)"
          >{{ tab.label }}</button>
        </div>
        <span class="sc-count" v-if="!loading">{{ filteredItems.length }} listing{{ filteredItems.length !== 1 ? 's' : '' }}</span>
      </div>

      <!-- Skeletons -->
      <div v-if="loading" class="listing-grid">
        <div v-for="n in 9" :key="n" class="listing-card">
          <div class="card-image skeleton" style="aspect-ratio:16/10;"></div>
          <div class="card-body">
            <div class="skeleton" style="height:17px;width:80%;margin-bottom:8px;border-radius:4px;"></div>
            <div class="skeleton" style="height:13px;width:55%;margin-bottom:20px;border-radius:4px;"></div>
            <div style="display:flex;align-items:center;justify-content:space-between;padding-top:12px;border-top:1px solid var(--border-light);">
              <div style="display:flex;align-items:center;gap:8px;">
                <div class="skeleton" style="height:28px;width:28px;border-radius:50%;"></div>
                <div class="skeleton" style="height:12px;width:80px;border-radius:4px;"></div>
              </div>
              <div class="skeleton" style="height:12px;width:48px;border-radius:4px;"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty -->
      <div v-else-if="!filteredItems.length" class="sc-empty-state">
        <div class="sc-empty-state__icon">🔍</div>
        <h3>No listings found</h3>
        <p v-if="search">No results for "<strong>{{ search }}</strong>". Try different keywords.</p>
        <p v-else>No listings available in this category yet.</p>
        <button v-if="search || activeType" class="btn-outline" @click="reset">Clear filters</button>
      </div>

      <!-- Grid -->
      <div v-else class="listing-grid">
        <a
          v-for="item in filteredItems"
          :key="item.id"
          :href="getDetailUrl(item)"
          class="listing-card"
        >
          <div class="card-image">
            <img :src="getItemImage(item)" :alt="getItemTitle(item)" @error="onImgError" />
            <span class="card-badge" :class="item.type">{{ formatType(item.type) }}</span>
          </div>
          <div class="card-body">
            <h3>{{ getItemTitle(item) }}</h3>
            <div class="card-meta">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              <span>{{ getItemLocation(item) }}</span>
            </div>
            <div class="card-footer">
              <div class="card-author">
                <img :src="getAvatarUrl(item.user)" :alt="item.user ? item.user.name : ''" @error="onAvatarError" />
                <span>{{ item.user ? item.user.name : '' }}</span>
              </div>
              <div class="card-stats">
                <span
                  :class="['card-likes-stat', item.likes && item.likes.length ? 'card-likes-stat--has' : '']"
                  @click.stop="item.likes && item.likes.length && openLikers(item.likes)"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" :fill="item.likes && item.likes.length ? '#e11d48' : 'none'" stroke="#e11d48" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                  {{ item.likes ? item.likes.length : 0 }}
                  <span v-if="item.likes && item.likes.length" class="card-likers-stack">
                    <img v-for="(l, i) in item.likes.slice(0, 3)" :key="l.id"
                      :src="l.user && l.user.avatar ? $avatarSrc(l.user.avatar, 'users') : initAvatar(l.user ? l.user.name : '?', 22)"
                      class="card-likers-avatar"
                      :style="{ marginLeft: i ? '-7px' : '4px', zIndex: 10 - i }"
                      @error="e => e.target.src = initAvatar(l.user ? l.user.name : '?', 22)"
                    />
                  </span>
                </span>
                <span>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                  {{ item.comments ? item.comments.length : 0 }}
                </span>
              </div>
            </div>
          </div>
        </a>
      </div>

      <!-- Load more -->
      <div v-if="!loading && hasMore" class="sc-load-more">
        <button class="btn-outline" @click="loadMore" :disabled="loadingMore">
          <span v-if="loadingMore" class="sc-spinner"></span>
          <span v-else>Load more listings</span>
        </button>
      </div>

    </div>

    <!-- Likers modal -->
    <teleport to="body">
      <div v-if="showLikersModal" class="likers-overlay" @click.self="showLikersModal = false">
        <div class="likers-modal">
          <div class="likers-modal__head">
            <span>Liked by</span>
            <button class="likers-modal__close" @click="showLikersModal = false">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <div class="likers-modal__list">
            <div v-for="l in selectedLikers" :key="l.id" class="likers-item">
              <img
                :src="l.user && l.user.avatar ? $avatarSrc(l.user.avatar, 'users') : initAvatar(l.user ? l.user.name : '?', 40)"
                class="likers-item__avatar"
                @error="e => e.target.src = initAvatar(l.user ? l.user.name : '?', 40)"
              />
              <div class="likers-item__info">
                <a v-if="l.user && l.user.slug" :href="'/sc/profile/' + l.user.slug" class="likers-item__name">{{ l.user.name }}</a>
                <span v-else class="likers-item__name">{{ l.user ? l.user.name : 'Unknown' }}</span>
                <span v-if="l.user && l.user.profession" class="likers-item__prof">{{ l.user.profession }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </teleport>

  </public-layout>
</template>

<script>
import PublicLayout from '@/layouts/PublicLayout.vue';
import { useSEO, SITE_ORIGIN } from '@/utils/seo';

export default {
  name: 'Listings',
  components: { PublicLayout },

  data() {
    return {
      imgUrl: process.env.VUE_APP_IMAGE_URL || '',
      apiUrl: process.env.VUE_APP_API_URL   || '/api/',

      tabs: [
        { label: 'All',        value: '' },
        { label: 'Projects',   value: 'project' },
        { label: 'Properties', value: 'property' },
        { label: 'Concepts',   value: 'plan' },
      ],

      activeType:    '',
      search:        '',
      items:         [],
      loading:       true,
      loadingMore:   false,
      currentPage:   1,
      lastPage:      1,
      searchTimer:   null,
      showLikersModal: false,
      selectedLikers:  [],
    };
  },

  computed: {
    filteredItems() {
      let list = this.items;
      if (this.activeType) {
        list = list.filter(i => i.type === this.activeType || (this.activeType === 'property' && i.type === 'post'));
      }
      const q = this.search.trim().toLowerCase();
      if (q) {
        list = list.filter(i => {
          const title = this.getItemTitle(i).toLowerCase();
          const loc   = this.getItemLocation(i).toLowerCase();
          return title.includes(q) || loc.includes(q);
        });
      }
      return list;
    },
    hasMore() {
      return this.currentPage < this.lastPage;
    },
  },

  mounted() {
    useSEO({
      title: 'Browse Listings — Projects, Properties & Concepts',
      description: 'Explore thousands of verified construction projects, properties for sale or rent, and architectural concepts across Africa on Structurecity.',
      url: SITE_ORIGIN + '/listings',
    });
    this.readQueryParams();
    this.loadPage(1);
  },

  methods: {
    readQueryParams() {
      const qs = new URLSearchParams(window.location.search);
      if (qs.get('type')) this.activeType = qs.get('type');
      if (qs.get('q'))    this.search     = qs.get('q');
    },

    loadPage(page) {
      if (page === 1) this.loading = true;
      else this.loadingMore = true;

      this.axios.get(this.apiUrl + 'feeds', { params: { page } })
        .then(res => {
          if (res.status === 200) {
            const payload = res.data;
            let newItems = [];
            if (Array.isArray(payload)) {
              newItems = payload;
              this.lastPage = 1;
            } else if (payload && payload.data) {
              newItems       = payload.data;
              this.lastPage  = payload.last_page || 1;
              this.currentPage = payload.current_page || page;
            }
            this.items = page === 1 ? newItems : [...this.items, ...newItems];
          }
        })
        .catch(() => {})
        .finally(() => { this.loading = false; this.loadingMore = false; });
    },

    loadMore() {
      if (this.loadingMore || !this.hasMore) return;
      this.loadPage(this.currentPage + 1);
    },

    setType(type) {
      this.activeType = type;
      this.updateUrl();
    },

    onSearchInput() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.updateUrl(), 300);
    },

    clearSearch() {
      this.search = '';
      this.updateUrl();
    },

    reset() {
      this.activeType = '';
      this.search     = '';
      this.updateUrl();
    },

    updateUrl() {
      const params = new URLSearchParams();
      if (this.activeType) params.set('type', this.activeType);
      if (this.search.trim()) params.set('q', this.search.trim());
      const qs  = params.toString();
      const url = '/listings' + (qs ? '?' + qs : '');
      window.history.replaceState({}, '', url);
    },

    getItemImage(item) {
      const fallback = 'https://placehold.co/600x380/e5e7eb/9ca3af?text=No+Image';
      const tryImages = (obj) => {
        if (!obj || !obj.images || !obj.images.length) return null;
        const img = obj.images[0];
        return img.path || (img.image ? this.imgUrl + img.image : null);
      };
      return tryImages(item.project) || tryImages(item.post) || tryImages(item.plan) || fallback;
    },

    getItemTitle(item) {
      return (item.project && item.project.name) || (item.post && item.post.name) || (item.plan && item.plan.name) || 'Untitled';
    },

    getItemLocation(item) {
      const src = item.project || item.post || item.plan;
      if (!src) return '';
      const city    = src.city    && src.city.name;
      const state   = src.state   && src.state.name;
      const country = src.country && src.country.name;
      return [city || state, country].filter(Boolean).join(', ') || 'Location N/A';
    },

    getDetailUrl(item) {
      if (item.project) return '/listing/project/'  + item.project.slug;
      if (item.post)    return '/listing/property/' + item.post.slug;
      if (item.plan)    return '/listing/plan/'     + item.plan.slug;
      return '/listings';
    },

    nameColor(name) {
      const palette = ['#4f46e5','#0891b2','#059669','#d97706','#7c3aed','#db2777','#ea580c','#0284c7'];
      let h = 0;
      for (const c of (name || '')) h = (h * 31 + c.charCodeAt(0)) & 0xffffffff;
      return palette[Math.abs(h) % palette.length];
    },
    initAvatar(name, size = 40) {
      const initials = (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
      const bg = this.nameColor(name);
      const fs = Math.round(size * 0.35);
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}"><rect width="${size}" height="${size}" rx="${Math.round(size * 0.22)}" fill="${bg}"/><text x="${size/2}" y="${size*0.64}" font-family="Arial,sans-serif" font-size="${fs}" font-weight="700" fill="#fff" text-anchor="middle">${initials}</text></svg>`;
      return 'data:image/svg+xml;base64,' + btoa(svg);
    },

    getAvatarUrl(user) {
      if (!user) return this.initAvatar('?');
      if (user.type === 'company' && user.company && user.company.logo) return this.imgUrl + user.company.logo;
      if (user.avatar) return this.$avatarSrc(user.avatar, 'users');
      const name = user.type === 'company' && user.company ? user.company.name : user.name;
      return this.initAvatar(name);
    },

    formatType(type) {
      return { project: 'Project', property: 'Property', post: 'Property', plan: 'Plan' }[type] || type;
    },

    openLikers(likes) {
      this.selectedLikers = likes;
      this.showLikersModal = true;
    },

    onImgError(e) { e.target.src = 'https://placehold.co/600x380/e5e7eb/9ca3af?text=No+Image'; },
    onAvatarError(e) { e.target.src = this.initAvatar(e.target.alt || '?'); },
  },
};
</script>

<style scoped>
/* ── Search bar (inside hero slot) ───────────────────── */
.sc-page-search {
  display: flex; align-items: center; gap: .75rem;
  background: rgba(255,255,255,.12); border: 1px solid rgba(255,255,255,.2);
  border-radius: var(--radius-full); padding: .75rem 1.25rem;
  width: 100%; max-width: 580px; backdrop-filter: blur(8px);
  transition: background .2s, border-color .2s;
}
.sc-page-search:focus-within { background: rgba(255,255,255,.18); border-color: rgba(255,255,255,.4); }
.sc-page-search svg { flex-shrink: 0; color: rgba(255,255,255,.6); }
.sc-page-search input {
  flex: 1; background: none; border: none; outline: none;
  font-family: var(--font); font-size: .9375rem; color: #fff; min-width: 0;
}
.sc-page-search input::placeholder { color: rgba(255,255,255,.5); }
.sc-page-search__clear {
  background: none; border: none; cursor: pointer; padding: 0; color: rgba(255,255,255,.6);
  display: flex; align-items: center; transition: color .15s;
}
.sc-page-search__clear:hover { color: #fff; }

/* ── Body layout ──────────────────────────────────────── */
.sc-listings-body { padding: 2rem 1.5rem 4rem; }
.sc-listings-toolbar {
  display: flex; align-items: center; justify-content: space-between;
  flex-wrap: wrap; gap: 1rem; margin-bottom: 2rem;
}
.sc-count { font-size: .875rem; color: var(--text-muted); font-weight: 500; }

/* ── Empty state ──────────────────────────────────────── */
.sc-empty-state {
  text-align: center; padding: 4rem 2rem; color: var(--text-muted);
}
.sc-empty-state__icon { font-size: 3rem; margin-bottom: 1rem; }
.sc-empty-state h3 { font-size: 1.25rem; font-weight: 700; color: var(--text); margin: 0 0 .5rem; }
.sc-empty-state p { margin: 0 0 1.5rem; font-size: .9375rem; }

/* ── Load more ────────────────────────────────────────── */
.sc-load-more { display: flex; justify-content: center; margin-top: 2.5rem; }

/* ── Spinner ──────────────────────────────────────────── */
.sc-spinner {
  display: inline-block; width: 16px; height: 16px;
  border: 2px solid rgba(0,0,0,.1); border-top-color: currentColor;
  border-radius: 50%; animation: spin .7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Card likes stat ──────────────────────────────────── */
.card-likes-stat { cursor: default; user-select: none; }
.card-likes-stat--has { cursor: pointer; }
.card-likes-stat--has:hover { color: var(--primary); }
.card-likers-stack { display: inline-flex; align-items: center; vertical-align: middle; }
.card-likers-avatar {
  width: 22px; height: 22px; border-radius: 50%;
  border: 2px solid #fff; object-fit: cover;
  position: relative; flex-shrink: 0;
}

/* ── Likers modal ─────────────────────────────────────── */
.likers-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,.45);
  z-index: 9999; display: flex; align-items: center; justify-content: center;
}
.likers-modal {
  background: #fff; border-radius: 12px; width: 100%; max-width: 380px;
  max-height: 70vh; display: flex; flex-direction: column;
  box-shadow: 0 20px 60px rgba(0,0,0,.18); overflow: hidden;
}
.likers-modal__head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 1rem 1.25rem; border-bottom: 1px solid var(--border-light);
  font-weight: 600; font-size: 1rem; color: var(--text);
}
.likers-modal__close {
  background: none; border: none; cursor: pointer; padding: 2px;
  color: var(--text-muted); display: flex; align-items: center;
}
.likers-modal__close:hover { color: var(--text); }
.likers-modal__list { overflow-y: auto; padding: .5rem 0; }
.likers-item {
  display: flex; align-items: center; gap: .75rem;
  padding: .625rem 1.25rem; transition: background .15s;
}
.likers-item:hover { background: var(--bg-soft, #f8f8f8); }
.likers-item__avatar {
  width: 40px; height: 40px; border-radius: 50%;
  object-fit: cover; flex-shrink: 0;
}
.likers-item__info { display: flex; flex-direction: column; min-width: 0; }
.likers-item__name {
  font-weight: 600; font-size: .875rem; color: var(--text);
  text-decoration: none; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
a.likers-item__name:hover { color: var(--primary); text-decoration: underline; }
.likers-item__prof { font-size: .75rem; color: var(--text-muted); margin-top: 1px; }

@media (max-width: 768px) {
  .sc-listings-toolbar { flex-direction: column; align-items: flex-start; }
  .likers-modal { max-width: calc(100vw - 2rem); }
}
</style>
